import { CheckIcon, Combobox, Popover, useCombobox } from '@mantine/core';
import { useClickOutside } from '@mantine/hooks';
import { useEffect, useState } from 'react';

// Generic Select Dropdown component
export default function SelectDropdownPart<T>({
  option,
  onClick,
  readonly,
  toggleEditing,
  updateFunc,
  options,
}: {
  onClick?: () => void;
  readonly?: boolean;
  toggleEditing: (editing: boolean) => void;
  option?: T;
  options: T[];
  updateFunc: (selected: T) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const combobox = useCombobox({
    onDropdownClose: () => {
      combobox.resetSelectedOption();
      combobox.focusTarget();
      toggleEditing(false);
      setEditing(false);
    },
    onDropdownOpen: (eventSource) => {
      if (eventSource === 'keyboard') {
        combobox.selectActiveOption();
      } else {
        combobox.updateSelectedOptionIndex('active');
      }
    },
  });

  const [value, setValue] = useState(option);
  useEffect(() => setValue(option), [option]);

  const handleKeyDown = (event: { key: string }) => {
    if (event.key === 'Escape') {
      toggleEditing(false);
      setEditing(false);
      setValue(option);
    }
  };

  const clickOutsidePopoverRef = useClickOutside(() => {
    toggleEditing(false);
    setEditing(false);
  });

  return (
    <div
      className="rounded-md px-1 hover:cursor-pointer"
      onKeyDown={handleKeyDown}
      onDoubleClick={() => {
        if (readonly) {
          return;
        }
        if (onClick) {
          onClick();
        }
        toggleEditing(true);
        setEditing(true);
      }}
    >
      <Popover
        width={150}
        position="bottom"
        withArrow
        shadow="md"
        opened={editing}
        closeOnClickOutside
        onClose={() => {
          toggleEditing(false);
          setEditing(false);
        }}
        onDismiss={() => {
          toggleEditing(false);
          setEditing(false);
        }}
      >
        <Popover.Target>
          <div className="flex items-center space-x-1 bg-transparent">
            <div className="text-yellow-900 dark:text-yellow-600">{String(value)}</div>
          </div>
        </Popover.Target>
        <Popover.Dropdown
          ref={clickOutsidePopoverRef}
          className="p-2 dark:bg-neutral-800"
          onClick={(e) => e.stopPropagation()}
        >
          <Combobox
            onOptionSubmit={async (optionValue) => {
              const matchedOption = options.find((opt) => String(opt) === optionValue);
              if (matchedOption) {
                setValue(matchedOption);
                toggleEditing(false);
                setEditing(false);
                await updateFunc(matchedOption);
              }
            }}
            store={combobox}
            resetSelectionOnOptionHover
          >
            <Combobox.Options>
              {options.map((opt) => {
                const isActive = opt === option;
                return (
                  <Combobox.Option
                    value={String(opt)}
                    key={String(opt)}
                    active={isActive}
                  >
                    <div className="flex items-center space-x-1">
                      {!!isActive && <CheckIcon size={12} />}
                      <div>{String(opt)}</div>
                    </div>
                  </Combobox.Option>
                );
              })}
            </Combobox.Options>
          </Combobox>
        </Popover.Dropdown>
      </Popover>
    </div>
  );
}
